import React from 'react';
import { cn } from '@/lib/utils';
import { useListNavigation } from '@/hooks/useListNavigation';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

/**
 * The list every page shows its rows in.
 *
 * Three things it does that our hand-rolled tables did not:
 *
 *   - the header sticks, so a long list scrolls under its own column names
 *     instead of losing them off the top;
 *   - you can type into the header to filter, which is how the old ERP the
 *     office came from worked and is far quicker than reaching for a select;
 *   - it can carry a totals row, because a list of money without a total is
 *     a list, not a report.
 *
 * Rows are 30px. That is the whole reason a page can show forty of them where
 * it used to show nine — no other change is needed once the row stops being a
 * card.
 *
 * Arrow-key navigation comes built in via useListNavigation, so every list that
 * moves to this component gets keyboard handling that previously had to be wired
 * up per page and so mostly was not.
 */

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' };

// A phone cannot show twelve columns without becoming a horizontal scroll bar
// with a table attached. `mobile: false` on a column drops it below md so the
// ones that identify the row survive at 375px; nothing is hidden by default.
const hiddenCell = (col) => col.mobile === false && 'hidden md:table-cell';

export function DataGrid({
  columns,
  rows,
  getRowId = (row, i) => row?.id ?? i,
  onRowClick,
  selectedId,
  rowClassName,
  filters,
  onFilterChange,
  sort,
  onSortChange,
  showTotals = false,
  empty = 'Nothing here yet',
  loading = false,
  className,
  'data-testid': testId,
}) {
  const filterable = onFilterChange && columns.some(c => c.filter);

  const { getListProps, getRowProps } = useListNavigation({
    count: rows.length,
    onSelect: onRowClick ? (index) => onRowClick(rows[index]) : undefined,
    enabled: !!onRowClick,
  });

  const toggleSort = (col) => {
    if (!col.sortable || !onSortChange) return;
    if (sort?.key !== col.key) return onSortChange({ key: col.key, dir: 'asc' });
    if (sort.dir === 'asc') return onSortChange({ key: col.key, dir: 'desc' });
    return onSortChange(null);
  };

  return (
    <div className={cn('min-h-0 min-w-0 flex-1 overflow-auto', className)}>
      <table className="w-full border-collapse text-[13px]" data-testid={testId}>
        <colgroup>
          {columns.map(col => (
            <col
              key={col.key}
              className={cn(col.mobile === false && 'hidden md:table-column')}
              style={col.width ? { width: col.width } : undefined}
            />
          ))}
        </colgroup>

        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-50">
            {columns.map(col => (
              <th
                key={col.key}
                scope="col"
                aria-sort={
                  !(col.sortable && onSortChange) ? undefined
                    : sort?.key !== col.key ? 'none'
                      : sort.dir === 'asc' ? 'ascending' : 'descending'
                }
                className={cn(
                  'whitespace-nowrap border-b border-slate-200 px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 sm:py-1.5 sm:text-[10px]',
                  ALIGN[col.align] || 'text-left',
                  col.sortable && onSortChange && 'select-none hover:text-brand-blue',
                  hiddenCell(col),
                  col.headerClassName
                )}
              >
                {/* sorting is a real button, not a click handler on the th —
                    otherwise the column order can only be changed with a mouse */}
                {React.createElement(
                  col.sortable && onSortChange ? 'button' : 'span',
                  {
                    ...(col.sortable && onSortChange
                      ? { type: 'button', onClick: () => toggleSort(col) }
                      : {}),
                    className: cn(
                      'inline-flex items-center gap-1 uppercase tracking-wider',
                      col.sortable && onSortChange && 'cursor-pointer hover:text-brand-blue',
                      col.align === 'right' && 'flex-row-reverse'
                    ),
                  },
                  col.header,
                  col.sortable && onSortChange && (
                    sort?.key !== col.key
                      ? <ChevronsUpDown key="i" className="h-3 w-3 opacity-30" />
                      : sort.dir === 'asc'
                        ? <ChevronUp key="i" className="h-3 w-3 text-brand-blue" />
                        : <ChevronDown key="i" className="h-3 w-3 text-brand-blue" />
                  )
                )}
              </th>
            ))}
          </tr>

          {filterable && (
            <tr className="bg-white">
              {columns.map(col => (
                <th key={col.key} scope="col" className={cn('border-b border-slate-200 p-0', hiddenCell(col))}>
                  {col.filter && (
                    <input
                      value={filters?.[col.key] ?? ''}
                      onChange={e => onFilterChange(col.key, e.target.value)}
                      placeholder="filter…"
                      aria-label={`Filter by ${col.header}`}
                      className={cn(
                        'w-full bg-transparent px-2.5 py-2 text-xs font-normal text-slate-700 placeholder:text-slate-300 sm:py-1 sm:text-[11px] focus:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-blue',
                        ALIGN[col.align] || 'text-left'
                      )}
                    />
                  )}
                </th>
              ))}
            </tr>
          )}
        </thead>

        <tbody {...(onRowClick ? getListProps() : {})}>
          {loading ? (
            <tr><td colSpan={columns.length} className="py-12 text-center">
              <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-brand-blue" />
            </td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="py-10 text-center text-xs text-slate-400">{empty}</td></tr>
          ) : rows.map((row, i) => {
            const id = getRowId(row, i);
            const selected = selectedId != null && selectedId === id;
            // A nested row — a line item under its order, a batch under its item —
            // stays in the same grid so its columns line up with the parent's
            // instead of becoming a card stack in a colSpan cell.
            const depth = row.__depth || 0;

            // A full-width row: an inline form under the record it belongs to,
            // which is how a stage gets closed without a dialog.
            if (row.__full) {
              return (
                <tr key={id} className="border-b border-slate-200 bg-blue-50/40" data-testid={`row-${id}`}>
                  <td colSpan={columns.length} className="px-2.5 py-2">{row.__render?.()}</td>
                </tr>
              );
            }

            return (
              <tr
                key={id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                {...(onRowClick ? getRowProps(i) : {})}
                className={cn(
                  'border-b border-slate-100 transition-colors',
                  onRowClick && 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring aria-selected:bg-slate-50',
                  depth > 0 && 'bg-slate-50/60 text-[12px]',
                  selected ? 'bg-blue-50/70' : 'hover:bg-slate-50/70',
                  typeof rowClassName === 'function' ? rowClassName(row) : rowClassName
                )}
                data-testid={`row-${id}`}
              >
                {columns.map((col, ci) => (
                  <td
                    key={col.key}
                    style={ci === 0 && depth ? { paddingLeft: `${0.625 + depth * 1.25}rem` } : undefined}
                    className={cn(
                      'px-2.5 py-3 align-middle text-slate-700 sm:py-1.5',
                      ALIGN[col.align] || 'text-left',
                      col.align === 'right' && 'tabular-nums',
                      hiddenCell(col),
                      col.cellClassName
                    )}
                  >
                    {col.render ? col.render(row, i) : row[col.key] ?? '—'}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>

        {showTotals && rows.length > 0 && (
          <tfoot className="sticky bottom-0">
            <tr className="bg-slate-100">
              {columns.map((col, i) => (
                <td
                  key={col.key}
                  className={cn(
                    'border-t-2 border-slate-300 px-2.5 py-2 text-[12px] font-semibold text-slate-800 sm:py-1.5',
                    ALIGN[col.align] || 'text-left',
                    col.align === 'right' && 'tabular-nums',
                    hiddenCell(col)
                  )}
                >
                  {col.total ? col.total(rows) : i === 0 ? 'Total' : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/**
 * Filter rows against whatever the user typed into the header. Case-insensitive
 * substring match on the column's own `filterValue`, falling back to the raw field.
 */
export function applyFilters(rows, columns, filters) {
  const active = Object.entries(filters || {}).filter(([, v]) => v?.trim?.());
  if (active.length === 0) return rows;
  return rows.filter(row => active.every(([key, term]) => {
    const col = columns.find(c => c.key === key);
    const raw = col?.filterValue ? col.filterValue(row) : row[key];
    return String(raw ?? '').toLowerCase().includes(term.trim().toLowerCase());
  }));
}

/** Sort rows by a column's `sortValue`, falling back to the raw field. */
export function applySort(rows, columns, sort) {
  if (!sort?.key) return rows;
  const col = columns.find(c => c.key === sort.key);
  const value = (row) => (col?.sortValue ? col.sortValue(row) : row[sort.key]);
  return [...rows].sort((a, b) => {
    const av = value(a), bv = value(b);
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sort.dir === 'desc' ? -cmp : cmp;
  });
}

export default DataGrid;
